import { slugify } from '@i22team/td-longtail-vue-components';
import {
  ProductChromaticity,
  ProductProviderKey,
  SortDirection,
  OfferSortField,
  KompaProductSortField,
} from '#shared/types/goliath/goliath.types';
import type {
  OfferFilterParams,
  OfferSearchParams,
  OfferSortParams,
  PaginationParams,
  KompaProductFilterParams,
  KompaProductSortParams,
} from '#shared/types/goliath/goliath.types';

// A slug is only valid if slugifying it is a no-op, e.g. "handys" or "smartphones-und-handys".
export const isSlug = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && slugify(value) === value;

// Category/product paths are slash-separated slugs, e.g. "zubehoer/kopfhoerer".
export const isSlugPath = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.split('/').every((segment) => isSlug(segment));

export const isId = (value: unknown): value is string | number =>
  (typeof value === 'string' && value.length > 0) || typeof value === 'number';

export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const isEnumValue =
  <T extends Record<string, string>>(enumObject: T) =>
  (value: unknown): value is T[keyof T] =>
    typeof value === 'string' && (Object.values(enumObject) as string[]).includes(value);

const isArrayOf =
  <T>(itemValidator: (value: unknown) => value is T) =>
  (value: unknown): value is T[] =>
    Array.isArray(value) && value.every(itemValidator);

const isOptional =
  <T>(check: (value: unknown) => value is T) =>
  (value: unknown): boolean =>
    value === undefined || value === null || check(value);

export const isSlugArray = isArrayOf(isSlug);
export const isSlugPathArray = isArrayOf(isSlugPath);
export const isIdArray = isArrayOf(isId);
export const isStringArray = isArrayOf(isNonEmptyString);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// A [dotted variable path, isValid] pair, e.g. ["filter.brandSlugs", false].
export type FieldCheck = [path: string, isValid: boolean];

export const allValid = (checks: FieldCheck[]): boolean => checks.every(([, isValid]) => isValid);

const isPositiveInteger = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) > 0;

// Only positive, whole page numbers/sizes make sense as pagination input.
const paginationParamsChecks = (value: unknown, path: string): FieldCheck[] => {
  if (!isPlainObject(value)) return [[path, false]];
  const { page, size } = value as PaginationParams;
  return [
    [`${path}.page`, isOptional(isPositiveInteger)(page)],
    [`${path}.size`, isOptional(isPositiveInteger)(size)],
  ];
};

export const isPaginationParams = (value: unknown): value is PaginationParams =>
  isPlainObject(value) && allValid(paginationParamsChecks(value, 'pagination'));

const offerSortParamsChecks = (value: unknown, path: string): FieldCheck[] => {
  if (!isPlainObject(value)) return [[path, false]];
  const { direction, field } = value as OfferSortParams;
  return [
    [`${path}.direction`, isEnumValue(SortDirection)(direction)],
    [`${path}.field`, isEnumValue(OfferSortField)(field)],
  ];
};

export const isOfferSortParams = (value: unknown): value is OfferSortParams =>
  isPlainObject(value) && allValid(offerSortParamsChecks(value, 'sort'));

// "fields"/"filters"/"notFilters"/"orFilters" are free-text search expressions, not slugs -
// only their basic shape (array of non-empty strings) is checked.
const offerSearchParamsChecks = (value: unknown, path: string): FieldCheck[] => {
  if (!isPlainObject(value)) return [[path, false]];
  const { fields, filters, notFilters, orFilters } = value as OfferSearchParams;
  return [
    [`${path}.fields`, isStringArray(fields)],
    [`${path}.filters`, isOptional(isStringArray)(filters)],
    [`${path}.notFilters`, isOptional(isStringArray)(notFilters)],
    [`${path}.orFilters`, isOptional(isStringArray)(orFilters)],
  ];
};

export const isOfferSearchParams = (value: unknown): value is OfferSearchParams =>
  isPlainObject(value) && allValid(offerSearchParamsChecks(value, 'complexSearch'));

// Checks only the "simple" scalar/array-of-scalar fields of OfferFilterParams, i.e. that
// they have the right JS type/shape. Slug-named fields are checked as plain strings rather
// than strict slug format, since not all of them are guaranteed to be dash-cased (e.g.
// compatibleProductFamilySlugs can contain spaces). Nested sub-filters (kompaProduct,
// onetimePrice, storageSizeRange, storageSizes) are structured objects rather than simple
// types and are intentionally left unchecked.
const offerFilterParamsChecks = (value: unknown, path: string): FieldCheck[] => {
  if (!isPlainObject(value)) return [[path, false]];
  const filter = value as OfferFilterParams;

  return [
    [
      `${path}.anyInGroupOfProductTagSlugs`,
      isOptional(isArrayOf(isStringArray))(filter.anyInGroupOfProductTagSlugs),
    ],
    [`${path}.articleNumbers`, isOptional(isIdArray)(filter.articleNumbers)],
    [`${path}.blacklistArticleNumbers`, isOptional(isStringArray)(filter.blacklistArticleNumbers)],
    [`${path}.brandSlugs`, isOptional(isStringArray)(filter.brandSlugs)],
    [
      `${path}.chromaticities`,
      isOptional(isArrayOf(isEnumValue(ProductChromaticity)))(filter.chromaticities),
    ],
    [`${path}.colorCodes`, isOptional(isStringArray)(filter.colorCodes)],
    [`${path}.colorSlugs`, isOptional(isStringArray)(filter.colorSlugs)],
    [`${path}.compatibleBrandSlugs`, isOptional(isStringArray)(filter.compatibleBrandSlugs)],
    [
      `${path}.compatibleProductFamilySlugs`,
      isOptional(isStringArray)(filter.compatibleProductFamilySlugs),
    ],
    [`${path}.discounted`, isOptional(isBoolean)(filter.discounted)],
    [`${path}.hasAlwaysDisplayed`, isOptional(isBoolean)(filter.hasAlwaysDisplayed)],
    [`${path}.listed`, isOptional(isBoolean)(filter.listed)],
    [`${path}.oneOfProductTagSlugs`, isOptional(isStringArray)(filter.oneOfProductTagSlugs)],
    [`${path}.productAvailable`, isOptional(isBoolean)(filter.productAvailable)],
    [`${path}.productCategoryPaths`, isOptional(isSlugPathArray)(filter.productCategoryPaths)],
    [
      `${path}.productProviders`,
      isOptional(isArrayOf(isEnumValue(ProductProviderKey)))(filter.productProviders),
    ],
    [`${path}.productTagSlugs`, isOptional(isStringArray)(filter.productTagSlugs)],
    [`${path}.productTags`, isOptional(isStringArray)(filter.productTags)],
  ];
};

export const isOfferFilterParams = (value: unknown): value is OfferFilterParams =>
  isPlainObject(value) && allValid(offerFilterParamsChecks(value, 'filter'));

export type OfferListQueryVariables = {
  filter?: OfferFilterParams | null;
  grouped?: boolean | null;
  sort?: OfferSortParams | null;
  pagination?: PaginationParams | null;
  search?: string | null;
  complexSearch?: OfferSearchParams | null;
};

// Shared shape of goliath queries built on top of the "offers" field (list, brand-aggregation, ...).
// Does not validate "validAt" (DateTime) - the GraphQL server already rejects malformed scalars for it.
export const offerListQueryVariablesChecks = (
  vars: OfferListQueryVariables | undefined
): FieldCheck[] => {
  if (!vars) return [];
  return [
    ...(vars.filter != null ? offerFilterParamsChecks(vars.filter, 'filter') : []),
    ['grouped', isOptional(isBoolean)(vars.grouped)],
    ...(vars.sort != null ? offerSortParamsChecks(vars.sort, 'sort') : []),
    ...(vars.pagination != null ? paginationParamsChecks(vars.pagination, 'pagination') : []),
    ['search', isOptional(isNonEmptyString)(vars.search)],
    ...(vars.complexSearch != null
      ? offerSearchParamsChecks(vars.complexSearch, 'complexSearch')
      : []),
  ];
};

export const isOfferListQueryVariables = (vars: OfferListQueryVariables | undefined): boolean =>
  allValid(offerListQueryVariablesChecks(vars));

// Unlike OfferSortParams, both fields of KompaProductSortParams are optional.
const kompaProductSortParamsChecks = (value: unknown, path: string): FieldCheck[] => {
  if (!isPlainObject(value)) return [[path, false]];
  const { direction, field } = value as KompaProductSortParams;
  return [
    [`${path}.direction`, isOptional(isEnumValue(SortDirection))(direction)],
    [`${path}.field`, isOptional(isEnumValue(KompaProductSortField))(field)],
  ];
};

// Checks only the "simple" scalar/array-of-scalar fields of KompaProductFilterParams.
// The dozens of nested per-category filters (adapters, appliances, cameras, ...) are
// structured objects rather than simple types and are intentionally left unchecked.
const kompaProductFilterParamsChecks = (value: unknown, path: string): FieldCheck[] => {
  if (!isPlainObject(value)) return [[path, false]];
  const filter = value as KompaProductFilterParams;

  return [
    [`${path}.brands`, isOptional(isStringArray)(filter.brands)],
    [`${path}.categories`, isOptional(isStringArray)(filter.categories)],
    [`${path}.compatibleAppVersions`, isOptional(isStringArray)(filter.compatibleAppVersions)],
    [`${path}.compatibleBaseStations`, isOptional(isStringArray)(filter.compatibleBaseStations)],
    [`${path}.eanLis`, isOptional(isStringArray)(filter.eanLis)],
    [`${path}.extension`, isOptional(isNonEmptyString)(filter.extension)],
    ...(filter.sort != null ? kompaProductSortParamsChecks(filter.sort, `${path}.sort`) : []),
  ];
};

export const isKompaProductFilterParams = (value: unknown): value is KompaProductFilterParams =>
  isPlainObject(value) && allValid(kompaProductFilterParamsChecks(value, 'filter'));

export type KompaListQueryVariables = {
  filter?: KompaProductFilterParams | null;
  pagination?: PaginationParams | null;
  search?: string | null;
};

export const kompaListQueryVariablesChecks = (
  vars: KompaListQueryVariables | undefined
): FieldCheck[] => {
  if (!vars) return [];
  return [
    ...(vars.filter != null ? kompaProductFilterParamsChecks(vars.filter, 'filter') : []),
    ...(vars.pagination != null ? paginationParamsChecks(vars.pagination, 'pagination') : []),
    ['search', isOptional(isNonEmptyString)(vars.search)],
  ];
};

export const isKompaListQueryVariables = (vars: KompaListQueryVariables | undefined): boolean =>
  allValid(kompaListQueryVariablesChecks(vars));
